import { z } from 'zod';

import { CollectionSchema } from './collections';
import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { SourceSchema } from './sources';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

/**
 * A file is a piece of information (pdf, html, text, etc...), files are immutable by nature in order to preserve historical references and accuracy over time
 *
 * Multiple files can exist for a single source, however this does not indicate a collection, but rather file revisions over time.
 */
export const FileRevisionSchema = z.object({
  fileRevisionId: PrimaryKeySchema,
  sourceId: SourceSchema.shape.sourceId,
  fileName: z.string().min(1).max(1000),
  fileType: z.enum(['pdf', 'html', 'json', 'txt', 'mbox', 'bin']),
  fileSize: z.number(),
  fileHash: z.string().min(1).max(255),
  createdAt: z.coerce.date(),
});

export type FileRevision = z.infer<typeof FileRevisionSchema>;

export const FileRevisionsSchema = z.array(FileRevisionSchema);

export type FileRevisions = z.infer<typeof FileRevisionsSchema>;

export const FileRevisionsQuerySchema = z
  .object({
    fileRevisionIds: commaDelimitedList(
      FileRevisionSchema.shape.fileRevisionId.array(),
    ).optional(),
    sourceIds: commaDelimitedList(
      SourceSchema.shape.sourceId.array(),
    ).optional(),
  })
  .merge(createOffsetPaginationRequestQuerySchema(z.enum(['createdAt'])));

export type FileRevisionsQuery = z.infer<typeof FileRevisionsQuerySchema>;

export const GetSignedDownloadUrlResponseSchema = z.object({
  downloadUrl: z.string(),
});

export type GetSignedDownloadUrlResponse = z.infer<
  typeof GetSignedDownloadUrlResponseSchema
>;

export const FileRevisionSearchResultSchema = z.object({
  fileRevisionId: FileRevisionSchema.shape.fileRevisionId,
  score: z.number(),
});

export type FileRevisionSearchResult = z.infer<
  typeof FileRevisionSearchResultSchema
>;

export const FileRevisionsSearchOptionsSchema = z.object({
  query: z.string(),
  collectionIds: commaDelimitedList(
    CollectionSchema.shape.collectionId.array(),
  ).optional(),
  limit: z.coerce.number().default(100).optional(),
});

export type FileRevisionsSearchOptions = z.infer<
  typeof FileRevisionsSearchOptionsSchema
>;

export const FileRevisionsSearchResultsSchema = z.array(
  FileRevisionSearchResultSchema,
);

export type FileRevisionsSearchResults = z.infer<
  typeof FileRevisionsSearchResultsSchema
>;
